import { requestGnbPcMegaMenuInfo, requestNewGnbInfo } from '@apis/renewal/gnb';
import { requestSearchInitSearchKeyword } from '@apis/renewal/search';
import type {
  GnbPcMegaMenuInfo,
  NewGnbInfoV2,
} from '@wconcept/wconcept-common/models/renewal/gnb';
import type { SearchKeywordInfo } from '@wconcept/wconcept-common/models/renewal/search';
import type { IRequestOptions } from '@apis/api';
import { requestMarketingPopup } from '@apis/display/popup';
import { getSsrDeviceValue } from './ssrDeviceUtil';
import { ApiError } from './customErrors';

interface DesktopGnbDataRequests {
  readonly requestMegaMenu: () => Promise<GnbPcMegaMenuInfo>;
  readonly requestSearchInit?: () => Promise<SearchKeywordInfo>;
}

interface DesktopGnbData {
  readonly megaMenu: GnbPcMegaMenuInfo;
  readonly searchInitInfo: SearchKeywordInfo | undefined;
}

export const loadDesktopGnbData = async ({
  requestMegaMenu,
  requestSearchInit,
}: DesktopGnbDataRequests): Promise<DesktopGnbData> => {
  const [megaMenu, searchInitInfo] = await Promise.all([
    requestMegaMenu(),
    requestSearchInit?.(),
  ]);
  return { megaMenu, searchInitInfo };
};

export const getServerSideGnbInfo = async ({
  optionParams = {},
  isMobileParams = false,
}: {
  optionParams?: IRequestOptions;
  isMobileParams?: boolean;
} = {}) => {
  let isMobile = !!isMobileParams;
  if (!optionParams.ssg) {
    const { headers } = await import('next/headers');
    const headersList = await headers();
    const userAgent = headersList.get('user-agent') as string;
    isMobile = getSsrDeviceValue(userAgent) !== 'PC';
  }
  let gnbInfo;
  let megaMenu: GnbPcMegaMenuInfo | undefined;
  let searchInitInfo: SearchKeywordInfo | undefined;
  let responseError: ApiError | undefined;
  try {
    gnbInfo = isMobile
      ? await requestNewGnbInfo({
          ...optionParams,
          headers: {
            ...optionParams.headers,
            deviceType: isMobile ? 'MOBILE' : 'PC',
          },
        })?.then((res: NewGnbInfoV2) => [
          ...(res?.mobileMenu?.windowN?.map(item => ({
            ...item,
            window: 'N' as const,
          })) || []),
          ...(res?.mobileMenu?.windowY?.map(item => ({
            ...item,
            window: 'Y' as const,
          })) || []),
        ])
      : await requestNewGnbInfo({
          ...optionParams,
          headers: {
            ...optionParams.headers,
            deviceType: isMobile ? 'MOBILE' : 'PC',
          },
        })?.then((res: NewGnbInfoV2) => res?.pcMenu || undefined);

    if (!isMobile) {
      const desktopGnbData = await loadDesktopGnbData({
        requestMegaMenu: () => requestGnbPcMegaMenuInfo(optionParams),
        requestSearchInit: () => requestSearchInitSearchKeyword(optionParams),
      });
      megaMenu = desktopGnbData.megaMenu;
      searchInitInfo = desktopGnbData.searchInitInfo;
    }
  } catch (e) {
    responseError = e as ApiError;
  } finally {
    if (
      responseError?.status &&
      !['513', '512'].includes(responseError?.status?.toString() ?? '')
    ) {
      // eslint-disable-next-line no-unsafe-finally
      throw responseError;
    }
    // eslint-disable-next-line no-unsafe-finally
    return {
      gnbInfo,
      megaMenu,
      searchInitInfo,
    };
  }
};

export const getClientSideGnbInfo = async () => {
  const { userAgent } = window.navigator;
  const isMobile = getSsrDeviceValue(userAgent) !== 'PC';
  let gnbInfo;
  let megaMenu: GnbPcMegaMenuInfo | undefined;
  let searchInitInfo: SearchKeywordInfo | undefined;
  let responseError: ApiError | undefined;
  let marketingPopup;
  try {
    marketingPopup = await requestMarketingPopup();
    gnbInfo = isMobile
      ? await requestNewGnbInfo()?.then((res: NewGnbInfoV2) => [
          ...(res?.mobileMenu?.windowN?.map(item => ({
            ...item,
            window: 'N' as const,
          })) || []),
          ...(res?.mobileMenu?.windowY?.map(item => ({
            ...item,
            window: 'Y' as const,
          })) || []),
        ])
      : await requestNewGnbInfo()?.then(
          (res: NewGnbInfoV2) => res?.pcMenu || undefined
        );

    if (!isMobile) {
      const desktopGnbData = await loadDesktopGnbData({
        requestMegaMenu: requestGnbPcMegaMenuInfo,
        requestSearchInit: requestSearchInitSearchKeyword,
      });
      megaMenu = desktopGnbData.megaMenu;
      searchInitInfo = desktopGnbData.searchInitInfo;
    }
  } catch (e) {
    responseError = e as ApiError;
  } finally {
    if (
      responseError?.status &&
      !['513', '512'].includes(responseError?.status?.toString() ?? '')
    ) {
      // eslint-disable-next-line no-unsafe-finally
      throw responseError;
    }
    // eslint-disable-next-line no-unsafe-finally
    return {
      gnbInfo,
      megaMenu,
      searchInitInfo,
      marketingPopup,
    };
  }
};
